import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Input, Select, Toggle } from '../../components/ui';
import { useListaProveedores, useListaVehiculos, useListaMaquinas, useListaOperadores, useListaInsumos, useListaTarjetas, useListaEmisores } from '../../hooks/useReferencias';
import { ordenesCargaCombustibleService } from '../../services/ordenesCargaCombustibleService';
import type { OrdenCargaCombustibleCreateRequest } from '../../types/ordenesCargaCombustible';

interface FormularioOrdenCargaProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (datos: OrdenCargaCombustibleCreateRequest) => void;
  isLoading?: boolean;
}

interface FormState {
  numero_orden: string;
  fecha_emision: string;
  proveedor_id: number;
  insumo_id: number;
  unidad: string;
  tarjeta_id: number;
  operador_id: number;
  emisor_id: number;
}

const valorInicial: FormState = {
  numero_orden: '',
  fecha_emision: new Date().toISOString().slice(0, 10),
  proveedor_id: 0,
  insumo_id: 0,
  unidad: 'LITROS',
  tarjeta_id: 0,
  operador_id: 0,
  emisor_id: 0,
};

export const FormularioOrdenCarga: React.FC<FormularioOrdenCargaProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
}) => {
  const { t } = useTranslation();
  const [datos, setDatos] = useState<FormState>(valorInicial);
  const [incluirVehiculo, setIncluirVehiculo] = useState(true);
  const [incluirMaquina, setIncluirMaquina] = useState(false);
  const [vehiculoId, setVehiculoId] = useState<number>(0);
  const [maquinaId, setMaquinaId] = useState<number>(0);
  const [cantidadVehiculo, setCantidadVehiculo] = useState<number>(0);
  const [cantidadMaquina, setCantidadMaquina] = useState<number>(0);

  const { data: proveedores } = useListaProveedores();
  const { data: vehiculos } = useListaVehiculos();
  const { data: maquinas } = useListaMaquinas();
  const { data: operadores } = useListaOperadores();
  const { data: insumos } = useListaInsumos();
  const { data: tarjetas } = useListaTarjetas();
  const { data: emisores } = useListaEmisores();

  const resetear = () => {
    setDatos(valorInicial);
    setIncluirVehiculo(true);
    setIncluirMaquina(false);
    setVehiculoId(0);
    setMaquinaId(0);
    setCantidadVehiculo(0);
    setCantidadMaquina(0);
  };

  // Al abrir, parte limpio (no arrastra datos de la orden anterior) y
  // precarga el correlativo sugerido (editable).
  useEffect(() => {
    if (isOpen) {
      resetear();
      ordenesCargaCombustibleService.siguienteNumero()
        .then((numero) => setDatos(prev => ({ ...prev, numero_orden: prev.numero_orden || numero })))
        .catch(() => { /* si falla, el usuario escribe el número a mano */ });
    }
  }, [isOpen]);

  const actualizar = (campo: keyof FormState, valor: string | number) => {
    setDatos(prev => ({ ...prev, [campo]: valor }));
  };

  const handleClose = () => {
    resetear();
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!esValido) return;
    onSubmit({
      ...datos,
      tarjeta_id: datos.tarjeta_id || undefined,
      emisor_id: datos.emisor_id || undefined,
      destinos: [
        ...(incluirVehiculo ? [{ vehiculo_id: vehiculoId, cantidad_autorizada: cantidadVehiculo }] : []),
        ...(incluirMaquina ? [{ maquina_id: maquinaId, cantidad_autorizada: cantidadMaquina }] : []),
      ],
    });
  };

  const esValido = datos.numero_orden.trim() !== '' &&
    datos.fecha_emision !== '' &&
    datos.proveedor_id > 0 &&
    datos.insumo_id > 0 &&
    datos.operador_id > 0 &&
    (incluirVehiculo || incluirMaquina) &&
    (!incluirVehiculo || (vehiculoId > 0 && cantidadVehiculo > 0)) &&
    (!incluirMaquina || (maquinaId > 0 && cantidadMaquina > 0));

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('ordenes_carga.form.title')} size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label={t('ordenes_carga.form.numero_orden')}
            value={datos.numero_orden}
            onChange={(e) => actualizar('numero_orden', e.target.value)}
            required
          />
          <Input
            type="date"
            label={t('ordenes_carga.form.fecha_emision')}
            value={datos.fecha_emision}
            onChange={(e) => actualizar('fecha_emision', e.target.value)}
            required
          />
          <Select
            label={t('ordenes_carga.form.proveedor')}
            value={datos.proveedor_id || ''}
            onChange={(e) => actualizar('proveedor_id', Number(e.target.value))}
            options={[
              { value: '', label: t('common.select') },
              ...(proveedores || []).map(p => ({ value: p.id, label: `${p.codigo} - ${p.nombre}` })),
            ]}
            required
          />
          <Select
            label={t('ordenes_carga.form.insumo')}
            value={datos.insumo_id || ''}
            onChange={(e) => actualizar('insumo_id', Number(e.target.value))}
            options={[
              { value: '', label: t('common.select') },
              ...(insumos || []).map(i => ({ value: i.id, label: `${i.codigo} - ${i.descripcion}` })),
            ]}
            required
          />
          <Input
            label={t('ordenes_carga.form.unidad')}
            value={datos.unidad}
            onChange={(e) => actualizar('unidad', e.target.value)}
          />
          <Select
            label={t('ordenes_carga.form.tarjeta')}
            value={datos.tarjeta_id || ''}
            onChange={(e) => actualizar('tarjeta_id', Number(e.target.value))}
            options={[
              { value: '', label: t('common.select') },
              ...(tarjetas || []).map(tj => ({
                value: tj.id,
                label: tj.compania ? `${tj.compania} - ${tj.numero}` : tj.numero,
              })),
            ]}
          />
          <Select
            label={t('ordenes_carga.form.usuario_autorizado')}
            value={datos.operador_id || ''}
            onChange={(e) => actualizar('operador_id', Number(e.target.value))}
            options={[
              { value: '', label: t('common.select') },
              ...(operadores || []).map(o => ({ value: o.id, label: `${o.codigo} - ${o.descripcion}` })),
            ]}
            required
          />
        </div>

        <div className="border border-gray-200 rounded-md p-3 space-y-3">
          <Toggle checked={incluirVehiculo} onChange={setIncluirVehiculo} label={t('ordenes_carga.solicitar.incluir_vehiculo')} />
          {incluirVehiculo && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
              <Select
                label={t('ordenes_carga.form.vehiculo')}
                value={vehiculoId || ''}
                onChange={(e) => setVehiculoId(Number(e.target.value))}
                options={[
                  { value: '', label: t('common.select') },
                  ...(vehiculos || []).map(v => ({ value: v.id, label: `${v.codigo} - ${v.patente}` })),
                ]}
                required
              />
              <Input
                type="number"
                step="0.01"
                min="0"
                label={t('ordenes_carga.form.cantidad_autorizada')}
                value={cantidadVehiculo || ''}
                onChange={(e) => setCantidadVehiculo(Number(e.target.value))}
                required
              />
            </div>
          )}
        </div>

        <div className="border border-gray-200 rounded-md p-3 space-y-3">
          <Toggle checked={incluirMaquina} onChange={setIncluirMaquina} label={t('ordenes_carga.solicitar.incluir_maquina')} />
          {incluirMaquina && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
              <Select
                label={t('ordenes_carga.table.maquina')}
                value={maquinaId || ''}
                onChange={(e) => setMaquinaId(Number(e.target.value))}
                options={[
                  { value: '', label: t('common.select') },
                  ...(maquinas || []).map((m: any) => ({ value: m.id, label: `${m.codigo} - ${m.descripcion || ''}` })),
                ]}
                required
              />
              <Input
                type="number"
                step="0.01"
                min="0"
                label={t('ordenes_carga.form.cantidad_autorizada')}
                value={cantidadMaquina || ''}
                onChange={(e) => setCantidadMaquina(Number(e.target.value))}
                required
              />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select
            label={t('ordenes_carga.form.emisor')}
            value={datos.emisor_id || ''}
            onChange={(e) => actualizar('emisor_id', Number(e.target.value))}
            options={[
              { value: '', label: t('common.select') },
              ...(emisores || []).map(em => ({
                value: em.id,
                label: em.cargo ? `${em.nombre} (${em.cargo})` : em.nombre,
              })),
            ]}
          />
        </div>

        <div className="flex justify-end space-x-3 pt-2">
          <Button variant="secondary" type="button" onClick={handleClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={!esValido} loading={isLoading}>
            {t('ordenes_carga.form.emitir')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default FormularioOrdenCarga;
